/**
 * Ecran de vente (PR 1.5a ; design 4.3, maquette vente.html).
 *
 * - Champ de scan TOUJOURS actif : il reprend le focus apres chaque action ;
 *   une touche tapee ailleurs (hors dialogue) y est renvoyee. Un lecteur
 *   (clavier HID) tape vite et finit par Entree.
 * - Entree : etiquette au poids (EAN-13 a prix integre, prefixe du catalogue),
 *   sinon sku (UPC-A / EAN-13) ; trouve : la ligne s'ajoute, bip court ;
 *   inconnu : bandeau rouge et bip grave, RIEN n'est ajoute. Un texte tape a
 *   la main cherche par nom (grosses tuiles).
 * - Quantite (moins, plus, pave), suppression, rabais selon les droits
 *   (vente/droits.js ; NIP d'un responsable au-dela du plafond), prix modifie
 *   avec le NIP d'un responsable, « Annuler la vente » avec confirmation.
 * - Raccourcis : F2 Encaisser, Echap Annuler (ou vider la saisie), F4 Rabais.
 * - Contrat (vente/contrat.js) : produit un PanierAEncaisser valide
 *   (verifierPanier renvoie []) et appelle onEncaisser(panier). Sans
 *   onEncaisser (PR 1.5a seule), « Encaisser comptant » est desactive.
 *   panierInitial : retour du paiement, meme panier (meme sale_uuid).
 *   onPanierNonVide(bool) : pour venteEnCours (UpdatePrompt blocked).
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CatalogStatus from '../app/CatalogStatus';
import { CONNECTION } from '../app/heartbeat';
import { verifyPinLogin } from '../app/pinLogin';
import { palette } from '../app/theme';
import { isoLocal, centsDepuisSaisie } from '../domain/money';
import { PORTEES_RABAIS, formatMontant, verifierPanier } from './contrat';
import { RESULTATS_SCAN, chargerCatalogue, chercherParNom, trouverParCode } from './catalogueLocal';
import {
    DECISIONS_RABAIS, MESSAGES_RABAIS, evaluerRabais, peutAutoriserRabais, peutFaireRabais, peutModifierPrix,
} from './droits';
import DialogueNip from './DialogueNip';
import DialogueRabais from './DialogueRabais';
import DialogueSaisie from './DialogueSaisie';
import LignePanier from './LignePanier';
import {
    ACTIONS_PANIER, PRIX_MAX_CENTS, QTE_MAX, calculerPanier, construirePanier, estAuPoids, etatDepuisPanier, panierReducer,
} from './panier';
import { SONS, jouerSon } from './sons';

// Lecteur de codes-barres : au moins 4 caracteres, moins de 35 ms entre deux.
const SCAN_MIN_CARACTERES = 4;
const SCAN_MAX_MS_PAR_CARACTERE = 35;
const SURBRILLANCE_MS = 1000;

export const MESSAGES_VENTE = Object.freeze({
    inconnu: (code) => `Code ${code} inconnu. Cherchez l'article par son nom ou appelez le responsable.`,
    illisible: (code) => `Étiquette au poids ${code} illisible. Rescannez-la ou appelez le responsable.`,
    aucunResultat: (texte) => `Aucun article ne correspond à « ${texte} ». Essayez un autre mot.`,
    chargement: 'Le catalogue se charge. Rescannez dans un instant.',
    panierInvalide: 'Cette vente ne peut pas être encaissée. Annulez-la et recommencez, ou appelez le responsable.',
    verrouillerPendantVente: 'Terminez ou annulez la vente avant de verrouiller la caisse.',
    prixInvalide: 'Entrez un prix entre 0,01 $ et 99 999,99 $.',
    quantiteInvalide: `Entrez une quantité de 1 à ${QTE_MAX}.`,
    droitPrix: 'Ce NIP n\'a pas le droit de modifier les prix.',
    etiquetteRequise: (nom) => `${nom} se vend au poids : scannez son étiquette de prix. Sans étiquette, appelez le responsable.`,
});

const TEXTES_PLEINS = /^\d+$/;

/** La saisie ressemble-t-elle a un code (pas a un nom) ? */
function ressembleAUnCode(texte) {
    return TEXTES_PLEINS.test(texte) && texte.length >= 6;
}

const MESSAGE_STYLES = {
    erreur: { bg: palette.errorTint, fg: palette.errorOnTint, Icone: ErrorOutlineIcon },
    info: { bg: palette.infoTint, fg: palette.infoOnTint, Icone: InfoOutlinedIcon },
};

export default function EcranVente({
    session, terminal, db, api, token, connection, catalogMeta, sync, onSync, canSync, onLock,
    onEncaisser, panierInitial = null, onPanierNonVide, onStatutPoste,
    verify = verifyPinLogin, nouvelUuid = () => globalThis.crypto.randomUUID(), maintenant = () => new Date(),
}) {
    const [state, dispatch] = useReducer(panierReducer, panierInitial, etatDepuisPanier);
    const [index, setIndex] = useState(null);
    const [saisie, setSaisie] = useState('');
    const [message, setMessage] = useState(null);
    // Ligne choisie au toucher ; la derniere ligne touchee (ajout, quantite)
    // reprend la main a l'action suivante. Calcule au rendu, sans effet : F4
    // juste apres un scan vise deja la nouvelle ligne.
    const [choix, setChoix] = useState(null);
    const [surbrillance, setSurbrillance] = useState(null);
    const [dialogue, setDialogue] = useState(null);
    const [nip, setNip] = useState(null);
    const inputRef = useRef(null);
    const frappe = useRef({ debut: 0, derniere: 0, n: 0 });
    const encaissement = useRef(false);

    const droits = session && session.rights;
    const snapshotId = catalogMeta ? catalogMeta.snapshot_id : null;
    const prefixe = catalogMeta ? catalogMeta.weight_barcode_prefix : null;
    const { lignes, totals } = useMemo(() => calculerPanier(state), [state]);
    const nonVide = lignes.length > 0;
    const dialogueOuvert = Boolean(dialogue || nip);

    // Catalogue du poste en memoire, relu quand la synchro en pose un nouveau.
    useEffect(() => {
        if (!db) return undefined;
        let annule = false;
        chargerCatalogue(db).then((idx) => { if (!annule) setIndex(idx); }).catch(() => { if (!annule) setIndex(null); });
        return () => { annule = true; };
    }, [db, snapshotId]);

    useEffect(() => {
        if (onPanierNonVide) onPanierNonVide(nonVide);
    }, [nonVide, onPanierNonVide]);
    useEffect(() => () => { if (onPanierNonVide) onPanierNonVide(false); }, [onPanierNonVide]);

    const selection = choix && choix.touche === state.touche && state.lignes.some((l) => l.id === choix.id)
        ? choix.id
        : state.derniere;
    const setSelection = (id) => setChoix(id === null ? null : { id, touche: state.touche });

    // Derniere ligne touchee : surlignee 1 s (design 4.3).
    useEffect(() => {
        if (state.derniere === null) return undefined;
        setSurbrillance(state.derniere);
        const id = setTimeout(() => setSurbrillance(null), SURBRILLANCE_MS);
        return () => clearTimeout(id);
    }, [state.derniere, state.touche]);

    useEffect(() => {
        if (state.avis) setMessage({ type: 'info', texte: state.avis });
    }, [state.avis]);

    // Le champ de scan reprend le focus apres chaque action.
    const reprendreFocus = useCallback(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) inputRef.current.focus();
    }, []);
    useEffect(() => {
        encaissement.current = false;
        if (!dialogueOuvert) reprendreFocus();
    }, [state, dialogueOuvert, reprendreFocus]);

    const signalerInconnu = useCallback((texte) => {
        setMessage({ type: 'erreur', inconnu: true, texte });
        jouerSon(SONS.CODE_INCONNU);
    }, []);

    const ajouterArticle = useCallback((resultat) => {
        // Article au poids sans etiquette (sku, recherche) : rien n'est ajoute.
        if (resultat.resultat !== RESULTATS_SCAN.POIDS && estAuPoids(resultat.article)) {
            signalerInconnu(MESSAGES_VENTE.etiquetteRequise(resultat.article.name));
            return;
        }
        const commun = { uuid: nouvelUuid(), at: isoLocal(maintenant()), article: resultat.article, scanned_code: resultat.code || null };
        if (resultat.resultat === RESULTATS_SCAN.POIDS) {
            dispatch({ type: ACTIONS_PANIER.AJOUTER_POIDS, ...commun, priceCents: resultat.priceCents });
        } else {
            dispatch({ type: ACTIONS_PANIER.AJOUTER, ...commun });
        }
        setMessage(null);
        jouerSon(SONS.SCAN_OK);
    }, [nouvelUuid, maintenant, signalerInconnu]);

    const traiterSaisie = useCallback((texte, rapide) => {
        const code = texte.trim();
        if (code === '') return;
        if (!index) {
            setMessage({ type: 'info', texte: MESSAGES_VENTE.chargement });
            return;
        }
        const r = trouverParCode(index, code, { prefixe });
        if (r.resultat === RESULTATS_SCAN.ARTICLE || r.resultat === RESULTATS_SCAN.POIDS
            || r.resultat === RESULTATS_SCAN.ETIQUETTE_REQUISE) {
            ajouterArticle(r);
            return;
        }
        if (r.resultat === RESULTATS_SCAN.ILLISIBLE) {
            signalerInconnu(MESSAGES_VENTE.illisible(code));
            return;
        }
        if (rapide || ressembleAUnCode(code)) {
            signalerInconnu(MESSAGES_VENTE.inconnu(code));
            return;
        }
        const trouves = chercherParNom(index, code);
        if (trouves.length === 1) {
            ajouterArticle({ resultat: RESULTATS_SCAN.ARTICLE, article: trouves[0], code: null });
        } else if (trouves.length === 0) {
            setMessage({ type: 'erreur', texte: MESSAGES_VENTE.aucunResultat(code) });
        } else {
            setSaisie(code);
        }
    }, [index, prefixe, ajouterArticle, signalerInconnu]);

    const surChangement = (event) => {
        const t = performance.now();
        const valeur = event.target.value;
        if (saisie === '' || valeur.length <= 1) frappe.current = { debut: t, derniere: t, n: valeur.length };
        else frappe.current = { ...frappe.current, derniere: t, n: valeur.length };
        setSaisie(valeur);
    };

    const surToucheChamp = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const { debut, derniere, n } = frappe.current;
        const rapide = n >= SCAN_MIN_CARACTERES && (derniere - debut) / Math.max(1, n - 1) < SCAN_MAX_MS_PAR_CARACTERE;
        const texte = saisie;
        setSaisie('');
        frappe.current = { debut: 0, derniere: 0, n: 0 };
        traiterSaisie(texte, rapide);
    };

    const resultats = useMemo(() => {
        const t = saisie.trim();
        if (!index || t.length < 2 || ressembleAUnCode(t)) return [];
        return chercherParNom(index, t);
    }, [index, saisie]);

    // --- Actions --------------------------------------------------------

    const encaisser = useCallback(() => {
        if (!onEncaisser || !nonVide || encaissement.current) return;
        const panier = construirePanier(state, {
            terminal_id: terminal.terminal_id,
            store_id: terminal.store_id,
            employee_id: session.employee_id,
            employee_display_name: session.display_name,
            catalog_snapshot_id: snapshotId,
        });
        if (verifierPanier(panier).length > 0) {
            setMessage({ type: 'erreur', texte: MESSAGES_VENTE.panierInvalide });
            return;
        }
        encaissement.current = true;
        onEncaisser(panier);
    }, [onEncaisser, nonVide, state, terminal, session, snapshotId]);

    const ouvrirRabais = useCallback(() => {
        if (!peutFaireRabais(droits)) {
            setMessage({ type: 'erreur', texte: MESSAGES_RABAIS.SANS_DROIT });
            jouerSon(SONS.CODE_INCONNU);
            return;
        }
        if (!nonVide) {
            setMessage({ type: 'info', texte: MESSAGES_RABAIS.RIEN });
            return;
        }
        setDialogue({ type: 'rabais', n: Date.now() });
    }, [droits, nonVide]);

    const demanderAnnulation = useCallback(() => {
        if (nonVide) setDialogue({ type: 'annuler' });
    }, [nonVide]);

    const verifierNip = useCallback(({ pin, requireManager }) => verify({
        db, api, token, pin, offline: connection === CONNECTION.OFFLINE, requireManager,
    }), [verify, db, api, token, connection]);

    const responsable = (e) => ({ employee_id: e.employee_id, display_name: e.display_name });
    const caissier = session ? responsable(session) : null;
    const ligneChoisie = lignes.find((_l, i) => state.lignes[i].id === selection) || null;
    const idDe = (ligne) => state.lignes[ligne.line_no - 1].id;

    const appliquerRabais = ({ portee, type, valeur }) => {
        const surVente = portee === PORTEES_RABAIS.VENTE;
        if (!surVente && !ligneChoisie) return MESSAGES_RABAIS.RIEN;
        const brutCents = surVente ? totals.gross_cents : ligneChoisie.gross_cents;
        const decision = evaluerRabais({ droits, type, valeur, brutCents });
        const action = (autorisePar) => ({
            type: surVente ? ACTIONS_PANIER.RABAIS_VENTE : ACTIONS_PANIER.RABAIS_LIGNE,
            id: surVente ? undefined : idDe(ligneChoisie),
            rabais: { type, valeur, par: caissier, autorise_par: autorisePar },
        });
        if (decision.decision === DECISIONS_RABAIS.OK) {
            dispatch(action(null));
            setDialogue(null);
            return null;
        }
        if (decision.decision === DECISIONS_RABAIS.RESPONSABLE) {
            setDialogue(null);
            setNip({
                n: Date.now(),
                titre: 'NIP du responsable',
                raison: MESSAGES_RABAIS.RESPONSABLE,
                exigerResponsable: true,
                droitSuffisant: peutAutoriserRabais,
                onAutorise: (e) => dispatch(action(responsable(e))),
            });
            return null;
        }
        return decision.message;
    };

    const retirerRabais = (portee) => {
        dispatch({ type: ACTIONS_PANIER.RETIRER_RABAIS, id: portee === PORTEES_RABAIS.VENTE ? null : idDe(ligneChoisie) });
        setDialogue(null);
    };

    const validerPrix = (ligne) => (texte) => {
        const cents = centsDepuisSaisie(texte);
        if (cents === null || cents < 1 || cents > PRIX_MAX_CENTS) return MESSAGES_VENTE.prixInvalide;
        const id = idDe(ligne);
        setDialogue(null);
        setNip({
            n: Date.now(),
            titre: 'NIP du responsable',
            raison: `Nouveau prix de ${ligne.name} : ${formatMontant(cents)}`,
            exigerResponsable: false,
            droitSuffisant: peutModifierPrix,
            refusDroit: MESSAGES_VENTE.droitPrix,
            onAutorise: (e) => dispatch({
                type: ACTIONS_PANIER.PRIX,
                id,
                override: { prix_cents: cents, prix_catalogue_cents: ligne.catalog_price_cents, par: responsable(e), at: isoLocal(maintenant()) },
            }),
        });
        return null;
    };

    const validerQuantite = (ligne) => (texte) => {
        const qte = /^\d{1,4}$/.test(texte) ? Number(texte) : NaN;
        if (!Number.isSafeInteger(qte) || qte < 1 || qte > QTE_MAX) return MESSAGES_VENTE.quantiteInvalide;
        dispatch({ type: ACTIONS_PANIER.QUANTITE, id: idDe(ligne), qty: qte });
        setDialogue(null);
        return null;
    };

    const verrouiller = () => {
        if (nonVide) {
            setMessage({ type: 'info', texte: MESSAGES_VENTE.verrouillerPendantVente });
            return;
        }
        onLock();
    };

    // --- Clavier ----------------------------------------------------------

    const clavier = useRef(null);
    clavier.current = (event) => {
        if (dialogueOuvert) return;
        if (event.key === 'F2') {
            event.preventDefault();
            encaisser();
        } else if (event.key === 'F4') {
            event.preventDefault();
            ouvrirRabais();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            if (saisie !== '') setSaisie('');
            else demanderAnnulation();
        } else if (event.target !== inputRef.current && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey
            && !(event.key === ' ' && event.target && event.target.closest && event.target.closest('button'))) {
            // Touche tapee hors du champ (un bouton avait le focus) : renvoyee au champ de scan.
            event.preventDefault();
            const t = performance.now();
            if (saisie === '') frappe.current = { debut: t, derniere: t, n: 1 };
            else frappe.current = { ...frappe.current, derniere: t, n: saisie.length + 1 };
            setSaisie((s) => s + event.key);
            reprendreFocus();
        }
    };
    useEffect(() => {
        const ecoute = (event) => clavier.current(event);
        window.addEventListener('keydown', ecoute);
        return () => window.removeEventListener('keydown', ecoute);
    }, []);

    // --- Rendu ------------------------------------------------------------

    const fermerDialogue = () => setDialogue(null);
    // Cle d'un dialogue : neuve a chaque ouverture, pour un etat neuf.
    const cle = (type) => (dialogue && dialogue.type === type ? `${type}-${dialogue.n}` : `${type}-ferme`);
    const ligneDuDialogue = dialogue && dialogue.ligne ? dialogue.ligne : null;
    const style = message ? MESSAGE_STYLES[message.type] : null;

    return (
        <Box data-testid="ecran-vente" data-catalogue={index ? 'pret' : 'chargement'} sx={{ flex: 1, display: 'flex', gap: 2, p: 2, minHeight: 0, overflowY: 'auto', bgcolor: palette.background }}>
            {/* Panier, 60 % */}
            <Box sx={{ flex: 6, display: 'flex', flexDirection: 'column', bgcolor: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 3, minHeight: 0, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: `1px solid ${palette.border}` }}>
                    <Typography component="h2" data-testid="titre-panier" sx={{ fontSize: 20, fontWeight: 800, color: palette.text }}>
                        {resultats.length > 0
                            ? `Résultats pour « ${saisie.trim()} »`
                            : `Vente en cours · ${totals.item_count} article${totals.item_count > 1 ? 's' : ''}`}
                    </Typography>
                    <Box
                        component="span"
                        data-testid="acces-vente"
                        data-acces="autorise"
                        data-raison=""
                        role="status"
                        sx={{ fontSize: 14, fontWeight: 700, px: 1.5, py: 0.5, borderRadius: 4, bgcolor: palette.successTint, color: palette.successOnTint }}
                    >
                        Prêt pour la vente
                    </Box>
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {resultats.length > 0 && (
                        <Box data-testid="resultats-recherche" sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, p: 2 }}>
                            {resultats.map((a) => (
                                <ButtonBase
                                    key={a.productcode}
                                    data-testid={`tuile-${a.productcode}`}
                                    onClick={() => { setSaisie(''); ajouterArticle({ resultat: RESULTATS_SCAN.ARTICLE, article: a, code: null }); }}
                                    sx={{
                                        minHeight: 96, p: 1.5, borderRadius: 2, border: `2px solid ${palette.border}`, bgcolor: palette.surface,
                                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between', textAlign: 'left',
                                        '&:hover': { borderColor: palette.primary },
                                    }}
                                >
                                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: palette.text, lineHeight: 1.2 }}>{a.name}</Typography>
                                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: palette.primary, mt: 1 }}>
                                        {formatMontant(a.weight ? (a.weight.price_per_unit_cents ?? a.price_cents) : a.price_cents)}
                                        {a.weight ? `/${a.weight.unit || 'kg'}` : ''}
                                    </Typography>
                                </ButtonBase>
                            ))}
                        </Box>
                    )}
                    {resultats.length === 0 && lignes.map((l, i) => (
                        <LignePanier
                            key={state.lignes[i].id}
                            ligne={l}
                            unite={state.lignes[i].unit || 'kg'}
                            surlignee={surbrillance === state.lignes[i].id}
                            selectionnee={selection === state.lignes[i].id}
                            onSelect={() => setSelection(state.lignes[i].id)}
                            onMoins={() => dispatch({ type: ACTIONS_PANIER.QUANTITE, id: state.lignes[i].id, qty: l.qty - 1 })}
                            onPlus={() => dispatch({ type: ACTIONS_PANIER.QUANTITE, id: state.lignes[i].id, qty: l.qty + 1 })}
                            onQuantite={() => setDialogue({ type: 'quantite', ligne: l, n: Date.now() })}
                            onPrix={() => setDialogue({ type: 'prix', ligne: l, n: Date.now() })}
                            onSupprimer={() => dispatch({ type: ACTIONS_PANIER.SUPPRIMER, id: state.lignes[i].id })}
                        />
                    ))}
                    {resultats.length === 0 && !nonVide && (
                        <Box data-testid="panier-vide" sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography component="p" data-testid="session-employe" sx={{ fontSize: 28, fontWeight: 800, color: palette.text }}>
                                Bonjour, {session.display_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: palette.successOnTint }}>
                                <CheckCircleIcon aria-hidden="true" />
                                <Typography sx={{ fontSize: 20 }}>Scannez un article ou cherchez-le par son nom.</Typography>
                            </Box>
                            {sync && (
                                <CatalogStatus
                                    catalogMeta={sync.catalogMeta}
                                    catalogSync={sync.catalogSync}
                                    syncing={sync.syncing}
                                    progress={sync.progress}
                                    onSync={onSync}
                                    canSync={canSync}
                                />
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Scan, totaux et actions, 40 % */}
            <Box sx={{ flex: 4, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Box sx={{ p: 2.25, bgcolor: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 3 }}>
                    <Typography component="label" htmlFor="champ-scan" sx={{ fontSize: 20, fontWeight: 800, color: palette.text }}>
                        Scannez ou cherchez un article
                    </Typography>
                    <Box
                        component="input"
                        id="champ-scan"
                        ref={inputRef}
                        data-testid="champ-scan"
                        autoFocus
                        autoComplete="off"
                        spellCheck={false}
                        placeholder="Code-barres ou nom…"
                        value={saisie}
                        onChange={surChangement}
                        onKeyDown={surToucheChamp}
                        onBlur={(e) => { if (!e.relatedTarget) setTimeout(() => { if (!document.querySelector('[role="dialog"]')) reprendreFocus(); }, 0); }}
                        sx={{
                            mt: 1.25, width: '100%', boxSizing: 'border-box', height: 64, border: `3px solid ${palette.primary}`, borderRadius: 3,
                            px: 2.25, fontSize: 20, color: palette.text, outline: 'none', boxShadow: '0 0 0 4px rgba(37,99,235,.15)', fontFamily: 'inherit',
                        }}
                    />
                    {message && (
                        <Box
                            role="alert"
                            data-testid="message-vente"
                            data-type={message.inconnu ? 'inconnu' : message.type}
                            sx={{ mt: 1.25, display: 'flex', gap: 1, alignItems: 'flex-start', bgcolor: style.bg, color: style.fg, borderRadius: 2.5, px: 1.75, py: 1.25 }}
                        >
                            <style.Icone sx={{ fontSize: 20, mt: 0.1 }} aria-hidden="true" />
                            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{message.texte}</Typography>
                        </Box>
                    )}
                </Box>

                <Box data-testid="totaux" sx={{ flex: 1, p: 2.75, bgcolor: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 3, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <LigneTotal libelle="Sous-total" montant={totals.subtotal_cents} testId="total-sous-total" />
                    {totals.discount_cents > 0 && (
                        <Typography data-testid="total-rabais" sx={{ fontSize: 15, color: palette.successOnTint, fontWeight: 600, textAlign: 'right', mt: -1, mb: 1 }}>
                            Rabais inclus : {formatMontant(totals.discount_cents)}
                        </Typography>
                    )}
                    <LigneTotal libelle="TPS 5 %" montant={totals.tps_cents} testId="total-tps" />
                    <LigneTotal libelle="TVQ 9,975 %" montant={totals.tvq_cents} testId="total-tvq" />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderTop: `2px solid ${palette.border}`, pt: 1.75, mt: 0.75 }}>
                        <Typography sx={{ fontSize: 24, fontWeight: 700, color: palette.text }}>TOTAL</Typography>
                        <Typography data-testid="total-vente" sx={{ fontSize: 64, fontWeight: 800, lineHeight: 1, color: palette.text }}>
                            {formatMontant(totals.total_cents)}
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant="contained"
                    data-testid="bouton-encaisser"
                    aria-keyshortcuts="F2"
                    disabled={!onEncaisser || !nonVide}
                    onClick={encaisser}
                    sx={{ minHeight: 88, fontSize: 26, fontWeight: 800, flexDirection: 'column', lineHeight: 1.2 }}
                >
                    {onEncaisser ? 'ENCAISSER COMPTANT · F2' : 'Encaisser comptant'}
                    {!onEncaisser && <Box component="span" sx={{ fontSize: 16, fontWeight: 600 }}>Paiement : prochaine version</Box>}
                </Button>
                <Box sx={{ display: 'grid', gridTemplateColumns: peutFaireRabais(droits) ? '1fr 1fr 1fr' : '1fr 1fr', gap: 1.5 }}>
                    {peutFaireRabais(droits) && (
                        <Button variant="outlined" onClick={ouvrirRabais} disabled={!nonVide} aria-keyshortcuts="F4" title="F4" sx={{ fontSize: 22 }}>Rabais</Button>
                    )}
                    <Button variant="outlined" onClick={demanderAnnulation} disabled={!nonVide} aria-keyshortcuts="Escape" title="Échap" sx={{ fontSize: 22, color: palette.errorOnTint }}>
                        Annuler
                    </Button>
                    <Button variant="outlined" startIcon={<LockOutlinedIcon />} onClick={verrouiller} sx={{ fontSize: 22 }}>Verrouiller</Button>
                </Box>
            </Box>

            <Dialog
                open={Boolean(dialogue && dialogue.type === 'annuler')}
                onClose={fermerDialogue}
                disableRestoreFocus
                aria-labelledby="dialogue-annuler-titre"
                data-testid="dialogue-annuler"
            >
                <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3, width: 460 }}>
                    <Typography id="dialogue-annuler-titre" component="h2" sx={{ fontSize: 26, fontWeight: 800, color: palette.text }}>
                        {`Annuler cette vente de ${totals.item_count} article${totals.item_count > 1 ? 's' : ''} ?`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button variant="contained" autoFocus onClick={fermerDialogue} sx={{ flex: 1 }}>Non, continuer</Button>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => { dispatch({ type: ACTIONS_PANIER.ANNULER }); setSelection(null); setMessage(null); setDialogue(null); }}
                            sx={{ flex: 1 }}
                        >
                            Oui, annuler
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            <DialogueRabais
                key={cle('rabais')}
                open={Boolean(dialogue && dialogue.type === 'rabais')}
                droits={droits}
                ligne={ligneChoisie}
                venteAUnRabais={Boolean(state.rabaisVente)}
                ligneAUnRabais={Boolean(ligneChoisie && ligneChoisie.discount)}
                onAppliquer={appliquerRabais}
                onRetirer={retirerRabais}
                onAnnuler={fermerDialogue}
            />

            <DialogueSaisie
                key={cle('quantite')}
                open={Boolean(dialogue && dialogue.type === 'quantite')}
                testId="dialogue-quantite"
                titre={ligneDuDialogue ? `Quantité : ${ligneDuDialogue.name}` : 'Quantité'}
                valeurInitiale=""
                onValider={ligneDuDialogue ? validerQuantite(ligneDuDialogue) : () => null}
                onAnnuler={fermerDialogue}
            />

            <DialogueSaisie
                key={cle('prix')}
                open={Boolean(dialogue && dialogue.type === 'prix')}
                testId="dialogue-prix"
                titre={ligneDuDialogue ? `Nouveau prix : ${ligneDuDialogue.name}` : 'Nouveau prix'}
                sousTitre={ligneDuDialogue
                    ? `Prix du catalogue : ${formatMontant(ligneDuDialogue.catalog_price_cents)} / unité. Le NIP d'un responsable sera demandé.`
                    : null}
                suffixe="$"
                decimale
                libelleValider="Continuer"
                onValider={ligneDuDialogue ? validerPrix(ligneDuDialogue) : () => null}
                onAnnuler={fermerDialogue}
                actionSecondaire={ligneDuDialogue && ligneDuDialogue.override ? {
                    libelle: 'Remettre le prix du catalogue',
                    onClick: () => { dispatch({ type: ACTIONS_PANIER.PRIX, id: idDe(ligneDuDialogue), override: null }); setDialogue(null); },
                } : null}
            />

            <DialogueNip
                key={nip ? `nip-${nip.n}` : 'nip-ferme'}
                open={Boolean(nip)}
                titre={nip ? nip.titre : ''}
                raison={nip ? nip.raison : ''}
                exigerResponsable={Boolean(nip && nip.exigerResponsable)}
                droitSuffisant={nip ? nip.droitSuffisant : null}
                refusDroit={nip ? nip.refusDroit : null}
                verifierNip={verifierNip}
                onAutorise={(e) => { const n = nip; setNip(null); n.onAutorise(e); }}
                onAnnuler={() => setNip(null)}
                onStatutPoste={onStatutPoste}
            />
        </Box>
    );
}

function LigneTotal({ libelle, montant, testId }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 20, mb: 1.25, color: palette.textSecondary }}>
            <Typography sx={{ fontSize: 20 }}>{libelle}</Typography>
            <Typography data-testid={testId} sx={{ fontSize: 20 }}>{formatMontant(montant)}</Typography>
        </Box>
    );
}
